import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatDate, statusLabel } from "@/lib/format";
import { toast } from "sonner";
import { ChevronDown, Download, Trash2, Loader2 } from "lucide-react";

const STATUSES = [
  { value: "en_attente", label: "En attente", color: "bg-tan/50 text-[#7c4a1e]" },
  { value: "confirmee", label: "Confirmée", color: "bg-sky2/50 text-teal-dark" },
  { value: "en_livraison", label: "En cours de livraison", color: "bg-indigo2/20 text-indigo2" },
  { value: "livree", label: "Livrée", color: "bg-mint/50 text-teal-dark" },
  { value: "annulee", label: "Annulée", color: "bg-[#b04a4a]/15 text-[#b04a4a]" },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/admin/orders/export?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commandes-${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const remove = async (order) => {
    if (!window.confirm(`Supprimer définitivement la commande ${order.ref} ? ${order.status === "annulee" ? "" : "Les produits seront remis en stock."}`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Commande ${order.ref} supprimée`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status });
      toast.success(status === "annulee" ? `Commande ${order.ref} annulée — produits remis en stock` : `Commande ${order.ref} — ${statusLabel(status)}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">Commandes</h1>
          <p className="text-sm text-ink/60 mt-1">Suivez chaque commande de la prise à la livraison. Paiement à la réception.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-full border border-line pl-4 pr-1.5 py-1.5">
          <label className="text-xs text-mauve font-mono uppercase tracking-widest">Mois</label>
          <input type="month" data-testid="admin-orders-export-month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-transparent text-sm outline-none" />
          <button data-testid="admin-orders-export" onClick={exportExcel} disabled={exporting} className="flex items-center gap-2 rounded-full bg-teal px-4 py-2 text-sm font-medium text-cream hover:bg-teal-dark transition-colors disabled:opacity-60">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Excel
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <p className="mt-10 bg-white rounded-3xl border border-line p-10 text-center text-ink/60">
          Aucune commande pour le moment — vos futures clientes passeront par la boutique en ligne.
        </p>
      ) : (
        <div className="mt-8 space-y-4">
          {orders.map((o) => {
            const st = STATUSES.find((s) => s.value === o.status) || STATUSES[0];
            const isOpen = expanded === o.id;
            return (
              <div key={o.id} data-testid={`admin-order-${o.ref}`} className="bg-white rounded-3xl border border-line overflow-hidden">
                <button
                  data-testid={`admin-order-toggle-${o.ref}`}
                  onClick={() => setExpanded(isOpen ? null : o.id)}
                  className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-4 text-left"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <span className={`rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-wider whitespace-nowrap ${st.color}`}>{st.label}</span>
                    <div className="min-w-0">
                      <p className="font-mono font-medium text-teal">{o.ref}</p>
                      <p className="text-sm text-ink/70 truncate">{o.customer_name} — {o.customer_phone}{o.customer_id && <span data-testid={`admin-order-account-${o.ref}`} className="ml-2 rounded-full bg-mint/40 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-teal-dark">Compte</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="font-semibold text-teal">{formatPrice(o.total)}</p>
                      <p className="text-xs text-mauve">{formatDate(o.created_at)}</p>
                    </div>
                    <ChevronDown size={18} className={`text-mauve transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-line px-5 md:px-7 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="eyebrow mb-2">Livraison</p>
                      <p className="text-sm text-ink/85">{o.customer_name}</p>
                      <p className="text-sm text-ink/85">{o.customer_phone}</p>
                      {o.customer_email && <p className="text-sm text-ink/85">{o.customer_email}</p>}
                      <p className="text-sm text-ink/85">{o.customer_address}{o.customer_zone ? ` — ${o.customer_zone}` : ""}</p>
                      {o.note && <p className="mt-2 text-sm text-ink/70 italic">« {o.note} »</p>}
                      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-mauve">Paiement à la livraison</p>
                    </div>
                    <div>
                      <p className="eyebrow mb-2">Articles</p>
                      <ul className="space-y-1.5 text-sm">
                        {o.items?.map((it, i) => (
                          <li key={i} className="flex justify-between gap-4 text-ink/85">
                            <span>{it.name}{it.variant && <span className="ml-1.5 rounded-full bg-mint/30 px-2 py-0.5 text-[11px] text-teal-dark">{it.variant}</span>} × {it.qty}</span>
                            <span className="text-teal font-medium whitespace-nowrap">{formatPrice(it.price * it.qty)}</span>
                          </li>
                        ))}
                        {o.discount > 0 && <div className="flex justify-between text-sm text-[#b04a4a]"><span>Remise {o.promo_code && `(${o.promo_code})`}</span><span>- {formatPrice(o.discount)}</span></div>}
                        <li className="flex justify-between gap-4 text-ink/70 pt-2 border-t border-line/60">
                          <span>Livraison</span><span>{o.delivery_fee ? formatPrice(o.delivery_fee) : <span className="text-xs text-mauve">à communiquer</span>}</span>
                        </li>
                        <li className="flex justify-between font-semibold text-teal">
                          <span>Total</span><span>{formatPrice(o.total)}</span>
                        </li>
                      </ul>
                      <label className="mt-4 block text-sm font-medium text-ink">
                        Statut
                        <select
                          data-testid={`admin-order-status-${o.ref}`}
                          value={o.status}
                          onChange={(e) => setStatus(o, e.target.value)}
                          className="mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-2.5 text-sm outline-none focus:border-teal"
                        >
                          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </label>
                      {o.status === "annulee" && <p className="mt-2 text-xs text-ink/60">Produits remis en stock.</p>}
                      <button
                        type="button"
                        data-testid={`admin-order-delete-${o.ref}`}
                        onClick={() => remove(o)}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[#b04a4a] hover:underline"
                      >
                        <Trash2 size={13} /> Supprimer cette commande
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
